'use client';

import { use } from 'react';

import styled from '@emotion/styled';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { SurveyThemeContext } from '@/shared-utilities';
import { type GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSectionMobileCompletion from './survey-ai-forms-ai-section-mobile-completion';
import SurveyAiFormsAiSectionMobileFooter from './survey-ai-forms-ai-section-mobile-footer';
import SurveyAiFormsAiSectionMobileHeader from './survey-ai-forms-ai-section-mobile-header';
import SurveyAiFormsAiSectionMobileQuestionRenderer from './survey-ai-forms-ai-section-mobile-question-renderer';
import { useAiSection } from './survey-ai-forms-ai-section.hooks';
import { useSwipeGesture } from './survey-ai-forms-swipe.hooks';
import {
  type FormAnswerValue,
  type ResponseMode,
  type ViewMode,
} from './survey-ai-forms.types';

const ThemedDiv = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

type ProjectQuestion = GetProjectQuery['project'] extends infer Project
  ? Project extends { questions: readonly (infer Question)[] }
    ? Question
    : never
  : never;

interface Props {
  project: GetProjectQuery['project'];
  currentQuestion: ProjectQuestion | null;
  currentQuestionIndex: number;
  totalQuestions: number;
  responseMode: ResponseMode;
  viewMode: ViewMode;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  onResponseSubmit: (value: FormAnswerValue) => void;
  onQuestionFocus?: (questionId: string) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  getTranscript: (questionId: string) => string;
  onViewModeToggle?: () => void;
  onSubmit: () => void;
  registerVoiceStopCallback?: (callback: (() => void) | null) => void;
  className?: string;
  isProcessingResponse?: boolean;
  isSubmitting?: boolean;
  isUserVoiceEnabled?: boolean;
  canSubmit?: boolean;
  isPreviewMode?: boolean;
  isAIFormAssistanceEnabled?: boolean;
}

export default function SurveyAiFormsAiSectionMobile({
  project,
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  responseMode,
  viewMode,
  progress,
  estimatedTotalTimeInSeconds,
  onResponseSubmit,
  onQuestionFocus,
  saveTranscript,
  getTranscript,
  onViewModeToggle,
  onSubmit,
  registerVoiceStopCallback,
  className = '',
  isProcessingResponse = false,
  isSubmitting = false,
  isUserVoiceEnabled = false,
  canSubmit: canSubmitFrom = false,
  isPreviewMode = false,
  isAIFormAssistanceEnabled = true,
}: Props) {
  const { styles } = use(SurveyThemeContext);

  const t = useTranslations('AIForms');

  const {
    transcriptValue,
    setTranscriptValue,
    currentValue,
    openEndedDisplayValue,
    hasValue,
    isFormattingTranscript,
    handleQuestionValueChange,
    handleOpenEndedChange,
    handleSubmit,
    handleAiNext,
  } = useAiSection({
    currentQuestion,
    questions: project?.questions ?? [],
    onResponseSubmit,
    saveTranscript,
    getTranscript,
    responseMode,
  });

  // Combined loading state: disable buttons during API calls
  // Voice: formatTranscriptAnswer + addFormInsight + addFormMessage
  // Non-voice: addFormInsight + addFormMessage
  const isLoading = isFormattingTranscript || isProcessingResponse;

  // Calculate canSubmit using the same logic as desktop question hooks
  const canSubmitQuestion =
    hasValue ||
    (transcriptValue ? transcriptValue.trim().length > 0 : false) ||
    (currentQuestion != null && currentQuestion.required !== true);

  const themeSettings = project.themeSettings;
  const hasBackgroundImage = !!themeSettings?.bgImage;

  // Swipe gesture for drag handle - swipe up to toggle to Form mode
  const swipeHandlers = useSwipeGesture({
    onSwipeUp: onViewModeToggle,
    threshold: 30, // 30px threshold for drag handle
  });

  if (!currentQuestion) {
    return (
      <div className={className} data-test='mobile-completion-wrapper'>
        <SurveyAiFormsAiSectionMobileCompletion
          onSubmit={onSubmit}
          totalQuestions={totalQuestions}
          questions={project.questions}
          onQuestionClick={onQuestionFocus}
          onViewModeToggle={onViewModeToggle}
          viewMode={viewMode}
          progress={progress}
          estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
          project={project}
          isSubmitting={isSubmitting}
          canSubmit={canSubmitFrom}
          isPreviewMode={isPreviewMode}
        />
      </div>
    );
  }

  return (
    <div
      data-test='mobile-section-wrapper'
      data-is-preview-mode={isPreviewMode}
      className='flex h-screen w-full flex-col px-2 pt-2 data-[is-preview-mode=true]:relative'
    >
      {/* Custom corner cutout */}
      <ThemedDiv
        data-test='mobile-section-cutout'
        data-has-background-image={hasBackgroundImage}
        data-is-preview-mode={isPreviewMode}
        className='absolute right-2 bottom-8 size-8 bg-(image:--brand-bg-image) bg-auto bg-bottom-right bg-no-repeat data-[has-background-image=false]:bg-(--brand-bg-color) data-[is-preview-mode=true]:bottom-9'
        themeStyles={styles}
      />

      {/* Main AI Section with Background */}
      <ThemedDiv
        data-test='mobile-section-main'
        data-has-background-image={hasBackgroundImage}
        className='relative flex flex-1 flex-col rounded-4xl rounded-br-none bg-(image:--brand-bg-image) bg-cover bg-center bg-no-repeat data-[has-background-image=false]:bg-(--brand-bg-color)'
        themeStyles={styles}
      >
        {/* Mobile Header - Logo and Progress Ring */}
        <SurveyAiFormsAiSectionMobileHeader
          progress={progress}
          estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
          viewMode={viewMode}
          onViewModeToggle={onViewModeToggle}
        />

        {/* Content - Centered question with 16px spacing */}
        <div className='mx-auto flex w-full max-w-180 flex-1 flex-col justify-center px-2'>
          <SurveyAiFormsAiSectionMobileQuestionRenderer
            question={currentQuestion}
            questionValue={currentValue}
            transcriptValue={transcriptValue}
            openEndedDisplayValue={openEndedDisplayValue}
            onQuestionValueChange={handleQuestionValueChange}
            onOpenEndedChange={handleOpenEndedChange}
            onTranscriptChange={setTranscriptValue}
            onSubmit={handleSubmit}
            onNext={handleAiNext}
            hasValue={hasValue}
            responseMode={responseMode}
            loading={isLoading}
            isMobile={true}
            isUserVoiceEnabled={isUserVoiceEnabled}
          />
        </div>

        {/* Mobile Footer - Reuse Composer Action Buttons */}
        <SurveyAiFormsAiSectionMobileFooter
          currentQuestion={currentQuestion}
          questions={project.questions}
          onResponseSubmit={onResponseSubmit}
          onNext={handleAiNext}
          onSubmit={handleSubmit} // Add handleSubmit for regular form submissions
          onQuestionFocus={onQuestionFocus}
          saveTranscript={saveTranscript}
          responseMode={responseMode}
          isVoiceEnabled={responseMode === 'voice'}
          isSupported={true}
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={totalQuestions}
          registerVoiceStopCallback={registerVoiceStopCallback}
          // Pass transcript state from parent
          transcriptValue={transcriptValue}
          setTranscriptValue={setTranscriptValue}
          isFormattingTranscript={isFormattingTranscript}
          // Pass computed values from hook
          hasValue={hasValue}
          canSubmit={canSubmitQuestion}
          isProcessingResponse={isProcessingResponse}
          isUserVoiceEnabled={isUserVoiceEnabled}
        />
      </ThemedDiv>

      {/* Turn off AI Section - Only show when AI is enabled and AI form assistance is enabled */}
      {isAIFormAssistanceEnabled && viewMode === 'ai' && onViewModeToggle && (
        <div className='z-10 h-16 rounded-4xl bg-white px-4 py-2'>
          <button
            onClick={onViewModeToggle}
            data-test='turn-off-ai-button'
            className='flex w-full flex-col items-center justify-center gap-2'
          >
            {/* Drag handle indicator - swipe up to toggle to Form mode */}
            <div
              className='h-1 w-12 cursor-grab rounded-lg bg-gray-300 active:cursor-grabbing'
              onTouchStart={swipeHandlers.onTouchStart}
              onTouchMove={swipeHandlers.onTouchMove}
              onTouchEnd={swipeHandlers.onTouchEnd}
              onPointerDown={swipeHandlers.onPointerDown}
              onPointerMove={swipeHandlers.onPointerMove}
              onPointerUp={swipeHandlers.onPointerUp}
            />

            <div className='flex items-center gap-2'>
              <FontAwesomeIcon
                icon={faSparkles}
                className='size-4 text-black'
              />

              <span className='[font-family:var(--brand-font)] text-base font-semibold text-black'>
                {t('modeToggle.turnOffAi')}
              </span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
